/**
 * AdminInvoices — Satış/Borç Kaydı ("invoices" koleksiyonu) / Cari Borç-Alacak listesi (Faz 2)
 *
 * NOT (etiket düzeltmesi, 2026-09-29, feature/parasut-fatura-faz4-web): bu
 * ekranda listelenen kayıt RESMİ FATURA DEĞİL — iç satış/borç kaydı (resmi
 * fatura Paraşüt'te kesiliyor, bkz. AdminParasutMapping.jsx). Görünen tüm
 * "Fatura" metinleri "Kayıt"/"Borç Kaydı" olarak değiştirildi; `invoice_id`,
 * `/admin/invoices` API path'i ve DB alan adları DEĞİŞMEDİ.
 *
 * Özet kartlar + durum filtresi + DataTable + Detay Modal + PDF İndir.
 * Veri: /admin/invoices (liste), /admin/invoices/{id} (detay), /admin/invoices/{id}/pdf.
 */
import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import AdminLayout from '../components/admin/AdminLayout';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getApiErrorMessage } from '../utils/apiError';
import { fmtTRY } from '../utils/formatCurrency';
import axios from 'axios';
import { PageHeader, DataTable, StatusBadge, LoadingState } from '../components/admin';
import {
  X,
  Download,
  FileText,
  ChevronDown,
  ChevronUp,
  ExternalLink,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ── Status metadata ──────────────────────────────────────────────────────── */
const STATUS_META = {
  issued:          { variant: 'danger',  tr: 'Açık',         en: 'Open'      },
  partially_paid:  { variant: 'warning', tr: 'Kısmi Ödendi', en: 'Partial'   },
  paid:            { variant: 'success', tr: 'Ödendi',        en: 'Paid'      },
  cancelled:       { variant: 'neutral', tr: 'İptal',         en: 'Cancelled' },
};

/* ── Kayıt tipi etiketi ───────────────────────────────────────────────────── */
const INVOICE_TYPE_KEY = {
  sale:     'admin.inv.type_sale',
  refund:   'admin.inv.type_refund',
  proforma: 'admin.inv.type_proforma',
};

/* ── Özet kart ────────────────────────────────────────────────────────────── */
function SummaryCard({ label, value, color, bg }) {
  return (
    <div className="rounded-2xl p-4 border-2" style={{ background: bg, borderColor: color + '33' }}>
      <p className="text-2xl font-bold" style={{ color }}>{value}</p>
      <p className="text-[11px] font-bold mt-1" style={{ color }}>{label}</p>
    </div>
  );
}

/* ── Detay satırı ─────────────────────────────────────────────────────────── */
function DetailRow({ label, children }) {
  return (
    <div className="flex flex-wrap gap-x-3 py-2 border-b border-gray-100 last:border-0">
      <span className="w-40 shrink-0 text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wide">
        {label}
      </span>
      <span className="flex-1 text-sm text-[var(--text-primary)]">{children}</span>
    </div>
  );
}

/* ── Kayıt Detay Modal ────────────────────────────────────────────────────── */
function InvoiceDetailModal({ invoiceId, onClose, authHeaders, lang, t, toast }) {
  const [detail, setDetail]         = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [showPayments, setShowPayments]     = useState(false);
  const [showInstallments, setShowInstallments] = useState(false);

  /* ── Detay yükle ────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (!invoiceId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${API}/admin/invoices/${invoiceId}`, {
          headers: authHeaders(),
        });
        if (!cancelled) setDetail(res.data);
      } catch (err) {
        if (!cancelled) setError(getApiErrorMessage(err, lang));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [invoiceId, authHeaders, lang]);

  /* ── PDF indir ──────────────────────────────────────────────────────────── */
  const downloadPdf = async () => {
    if (pdfLoading) return;
    setPdfLoading(true);
    try {
      const res = await axios.get(`${API}/admin/invoices/${invoiceId}/pdf`, {
        headers: authHeaders(),
        responseType: 'blob',
      });
      /* Content-Disposition'dan dosya adını al, yoksa fatura_no.pdf kullan */
      const cd = res.headers?.['content-disposition'] || '';
      const match = cd.match(/filename[^;=\n]*=["']?([^"';\n]+)["']?/i);
      const filename = match ? match[1].trim() : `${detail?.invoice_no || invoiceId}.pdf`;

      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error(t('admin.inv.pdf_error'));
    } finally {
      setPdfLoading(false);
    }
  };

  /* ── Tip etiketi ────────────────────────────────────────────────────────── */
  const typeLabel = (type) => t(INVOICE_TYPE_KEY[type] || 'admin.inv.type_sale');

  /* ── Durum rozeti ───────────────────────────────────────────────────────── */
  const statusBadge = (status) => {
    const m = STATUS_META[status] || { variant: 'neutral', tr: status, en: status };
    return <StatusBadge variant={m.variant} label={lang === 'tr' ? m.tr : m.en} />;
  };

  /* ── Para formatı ───────────────────────────────────────────────────────── */
  const money = (n) => fmtTRY(n);

  /* ── Tarih formatı ──────────────────────────────────────────────────────── */
  const fmtDate = (v) => (v ? new Date(v).toLocaleDateString(lang === 'tr' ? 'tr-TR' : 'en-GB') : '-');

  return (
    <AnimatePresence>
      {/* Overlay */}
      <motion.div
        key="inv-overlay"
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        aria-label={t('admin.inv.close')}
      >
        {/* Panel */}
        <motion.div
          key="inv-panel"
          role="dialog"
          aria-modal="true"
          aria-label={t('admin.inv.detail_title')}
          className="bg-white rounded-2xl border-2 border-gray-200 shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          initial={{ opacity: 0, y: 32, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 16, scale: 0.97 }}
          transition={{ type: 'spring', damping: 22, stiffness: 280 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Başlık */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white z-10 rounded-t-2xl">
            <div className="flex items-center gap-2">
              <FileText size={18} className="text-[var(--brand-primary)]" />
              <h2 className="text-base font-bold text-[var(--text-primary)]">
                {t('admin.inv.detail_title')}
                {detail?.invoice_no && (
                  <span className="ml-2 text-xs font-mono text-[var(--brand-primary)]">
                    {detail.invoice_no}
                  </span>
                )}
              </h2>
            </div>
            <div className="flex items-center gap-2">
              {/* PDF indir butonu — başlıkta */}
              {detail && (
                <motion.button
                  id="inv-pdf-btn"
                  aria-label={t('admin.inv.pdf_download')}
                  onClick={downloadPdf}
                  disabled={pdfLoading}
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.96 }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border-2 border-[var(--brand-primary)] bg-[#E8F5FD] text-[var(--brand-primary)] text-xs font-bold transition disabled:opacity-60"
                >
                  <Download size={15} />
                  {pdfLoading ? '...' : t('admin.inv.pdf_download')}
                </motion.button>
              )}
              <motion.button
                id="inv-close-btn"
                aria-label={t('admin.inv.close')}
                onClick={onClose}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className="p-1.5 rounded-xl text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-gray-100 transition"
              >
                <X size={18} />
              </motion.button>
            </div>
          </div>

          {/* İçerik */}
          <div className="px-5 py-4 space-y-5">
            {/* Loading */}
            {loading && (
              <div className="py-8 text-center text-sm text-[var(--text-muted)]">
                {t('admin.inv.loading')}
              </div>
            )}

            {/* Hata */}
            {!loading && error && (
              <div className="py-8 text-center text-sm text-red-500">
                {error || t('admin.inv.load_error')}
              </div>
            )}

            {/* Detay */}
            {!loading && !error && detail && (
              <>
                {/* ── Genel Bilgiler ────────────────────────────────────── */}
                <section aria-label={t('admin.inv.detail_title')}>
                  <DetailRow label={t('admin.inv.field_invoice_no')}>
                    <span className="font-mono font-bold">{detail.invoice_no || '-'}</span>
                  </DetailRow>
                  <DetailRow label={t('admin.inv.field_type')}>
                    {typeLabel(detail.invoice_type)}
                  </DetailRow>
                  <DetailRow label={t('admin.inv.field_status')}>
                    {statusBadge(detail.status)}
                  </DetailRow>
                  <DetailRow label={t('admin.inv.field_customer')}>
                    <span>{detail.user_name || detail.customer_name || '-'}</span>
                    {(detail.user_tc || detail.customer_tc) && (
                      <span className="ml-2 text-[10px] text-[var(--text-muted)]">
                        {t('admin.inv.field_tc')}: {detail.user_tc || detail.customer_tc}
                      </span>
                    )}
                  </DetailRow>
                  <DetailRow label={t('admin.inv.field_issued_at')}>
                    {fmtDate(detail.issued_at)}
                  </DetailRow>
                  <DetailRow label={t('admin.inv.field_payment_method')}>
                    {detail.payment_method || '-'}
                  </DetailRow>
                </section>

                {/* ── Kalem Tablosu ─────────────────────────────────────── */}
                <section aria-label={t('admin.inv.lines_title')}>
                  <h3 className="text-xs font-bold uppercase tracking-wide text-[var(--text-muted)] mb-2">
                    {t('admin.inv.lines_title')}
                  </h3>
                  {(!detail.line_items || detail.line_items.length === 0) ? (
                    <p className="text-sm text-[var(--text-muted)] py-2">{t('admin.inv.no_lines')}</p>
                  ) : (
                    <div className="overflow-x-auto rounded-xl border border-gray-100">
                      <table className="w-full text-xs" role="table">
                        <thead>
                          <tr className="bg-gray-50 text-[var(--text-muted)] uppercase tracking-wide">
                            <th className="px-3 py-2 text-left font-semibold" scope="col">
                              {t('admin.inv.col_desc')}
                            </th>
                            <th className="px-3 py-2 text-right font-semibold" scope="col">
                              {t('admin.inv.col_qty')}
                            </th>
                            <th className="px-3 py-2 text-right font-semibold" scope="col">
                              {t('admin.inv.col_unit_price')}
                            </th>
                            <th className="px-3 py-2 text-right font-semibold" scope="col">
                              {t('admin.inv.col_vat_rate')}
                            </th>
                            <th className="px-3 py-2 text-right font-semibold" scope="col">
                              {t('admin.inv.col_vat_amount')}
                            </th>
                            <th className="px-3 py-2 text-right font-semibold" scope="col">
                              {t('admin.inv.col_line_total')}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {detail.line_items.map((item, idx) => (
                            <tr key={idx} className="border-t border-gray-100 hover:bg-gray-50">
                              <td className="px-3 py-2 text-left">{item.description || '-'}</td>
                              <td className="px-3 py-2 text-right">{item.qty ?? item.quantity ?? 1}</td>
                              <td className="px-3 py-2 text-right">{money(item.unit_price)}</td>
                              <td className="px-3 py-2 text-right">
                                {item.vat_rate != null ? `%${item.vat_rate}` : '-'}
                              </td>
                              <td className="px-3 py-2 text-right">{money(item.vat_amount)}</td>
                              <td className="px-3 py-2 text-right font-semibold">{money(item.line_total)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </section>

                {/* ── Toplamlar ─────────────────────────────────────────── */}
                <section aria-label={t('admin.inv.grand_total')} className="bg-gray-50 rounded-xl p-4 space-y-1.5">
                  <div className="flex justify-between text-sm text-[var(--text-muted)]">
                    <span>{t('admin.inv.subtotal')}</span>
                    <span>{money(detail.subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-[var(--text-muted)]">
                    <span>{t('admin.inv.vat_total')}</span>
                    <span>{money(detail.vat_amount)}</span>
                  </div>
                  <div className="flex justify-between text-base font-bold text-[var(--text-primary)] border-t border-gray-200 pt-1.5 mt-1">
                    <span>{t('admin.inv.grand_total')}</span>
                    <span className="text-[var(--brand-primary)]">{money(detail.grand_total ?? detail.total_amount)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-emerald-600 font-semibold">
                    <span>{t('admin.inv.paid_amount')}</span>
                    <span>{money(detail.paid_amount)}</span>
                  </div>
                  {Number(detail.remaining_amount) > 0 && (
                    <div className="flex justify-between text-sm text-red-500 font-bold">
                      <span>{t('admin.inv.remaining_amount')}</span>
                      <span>{money(detail.remaining_amount)}</span>
                    </div>
                  )}
                </section>

                {/* ── Ödemeler (collapsible) ────────────────────────────── */}
                {detail.payments && detail.payments.length > 0 && (
                  <section aria-label={t('admin.inv.payments_title')}>
                    <button
                      onClick={() => setShowPayments((v) => !v)}
                      className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-[var(--text-muted)] hover:text-[var(--text-primary)] transition w-full text-left"
                      aria-expanded={showPayments}
                    >
                      {showPayments ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      {t('admin.inv.payments_title')} ({detail.payments.length})
                    </button>
                    <AnimatePresence>
                      {showPayments && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <div className="mt-2 overflow-x-auto rounded-xl border border-gray-100">
                            <table className="w-full text-xs" role="table">
                              <thead>
                                <tr className="bg-gray-50 text-[var(--text-muted)] uppercase tracking-wide">
                                  <th className="px-3 py-2 text-left font-semibold" scope="col">{t('admin.inv.pay_date')}</th>
                                  <th className="px-3 py-2 text-right font-semibold" scope="col">{t('admin.inv.pay_amount')}</th>
                                  <th className="px-3 py-2 text-left font-semibold" scope="col">{t('admin.inv.pay_method')}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {detail.payments.map((p, idx) => (
                                  <tr key={idx} className="border-t border-gray-100 hover:bg-gray-50">
                                    <td className="px-3 py-2">{fmtDate(p.paid_at || p.date || p.created_at)}</td>
                                    <td className="px-3 py-2 text-right font-semibold">{money(p.amount)}</td>
                                    <td className="px-3 py-2">{p.payment_method || p.method || '-'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </section>
                )}

                {/* ── Taksitler (collapsible) ───────────────────────────── */}
                {detail.installments && detail.installments.length > 0 && (
                  <section aria-label={t('admin.inv.installments_title')}>
                    <button
                      onClick={() => setShowInstallments((v) => !v)}
                      className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-[var(--text-muted)] hover:text-[var(--text-primary)] transition w-full text-left"
                      aria-expanded={showInstallments}
                    >
                      {showInstallments ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      {t('admin.inv.installments_title')} ({detail.installments.length})
                    </button>
                    <AnimatePresence>
                      {showInstallments && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <div className="mt-2 overflow-x-auto rounded-xl border border-gray-100">
                            <table className="w-full text-xs" role="table">
                              <thead>
                                <tr className="bg-gray-50 text-[var(--text-muted)] uppercase tracking-wide">
                                  <th className="px-3 py-2 text-right font-semibold" scope="col">{t('admin.inv.inst_no')}</th>
                                  <th className="px-3 py-2 text-left font-semibold" scope="col">{t('admin.inv.inst_due')}</th>
                                  <th className="px-3 py-2 text-right font-semibold" scope="col">{t('admin.inv.inst_amount')}</th>
                                  <th className="px-3 py-2 text-left font-semibold" scope="col">{t('admin.inv.inst_status')}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {detail.installments.map((inst, idx) => (
                                  <tr key={idx} className="border-t border-gray-100 hover:bg-gray-50">
                                    <td className="px-3 py-2 text-right">{inst.installment_no ?? inst.number ?? (idx + 1)}</td>
                                    <td className="px-3 py-2">{fmtDate(inst.due_date)}</td>
                                    <td className="px-3 py-2 text-right font-semibold">{money(inst.amount)}</td>
                                    <td className="px-3 py-2">
                                      {statusBadge(inst.status)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </section>
                )}
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

/* ── Ana Sayfa ────────────────────────────────────────────────────────────── */
export default function AdminInvoices() {
  const { lang, t } = useLanguage();
  const { authHeaders } = useAuth();
  const toast = useToast();

  const [invoices, setInvoices]     = useState([]);
  const [summary, setSummary]       = useState({
    total_billed: 0, total_collected: 0, total_outstanding: 0, invoice_count: 0,
  });
  const [status, setStatus]         = useState('');
  const [loading, setLoading]       = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const q = status ? `?status=${status}&limit=200` : '?limit=200';
      const [listRes, sumRes] = await Promise.all([
        axios.get(`${API}/admin/invoices${q}`, { headers: authHeaders() }),
        axios.get(`${API}/admin/invoices/summary`, { headers: authHeaders() }),
      ]);
      setInvoices(listRes.data?.invoices || []);
      setSummary(sumRes.data || {});
    } catch (err) {
      console.error('Faturalar yüklenemedi:', err);
    } finally {
      setLoading(false);
    }
  }, [status, authHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const money = (n) => fmtTRY(n);

  /* ── Tablo sütunları ────────────────────────────────────────────────────── */
  const columns = [
    {
      key: 'invoice_no',
      label: t('admin.inv.field_invoice_no'),
      render: (v, r) => (
        <span className="font-mono text-xs font-bold text-[var(--brand-primary)]">
          {v || r.invoice_id?.slice(0, 14) || '-'}
        </span>
      ),
    },
    {
      key: 'user_name',
      label: lang === 'tr' ? 'Öğrenci' : 'Student',
      render: (v, r) => (
        <div>
          <div className="font-semibold">{v || r.customer_name || '—'}</div>
          <div className="text-[10px] text-[var(--text-muted)]">{r.user_email || '—'}</div>
        </div>
      ),
    },
    {
      key: 'description',
      label: lang === 'tr' ? 'Kurs / Açıklama' : 'Course / Description',
      render: (v, r) => (
        <span className="text-xs">
          {r.course_title || v || '—'}
        </span>
      ),
    },
    {
      key: 'total_amount',
      label: lang === 'tr' ? 'Tutar' : 'Total',
      sortable: true,
      render: money,
    },
    {
      key: 'paid_amount',
      label: t('admin.inv.paid_amount'),
      sortable: true,
      render: money,
    },
    {
      key: 'remaining_amount',
      label: t('admin.inv.remaining_amount'),
      sortable: true,
      render: (v) => (
        <span className={Number(v) > 0 ? 'font-bold text-red-500' : ''}>{money(v)}</span>
      ),
    },
    {
      key: 'status',
      label: t('admin.inv.field_status'),
      render: (v) => {
        const m = STATUS_META[v] || { variant: 'neutral', tr: v, en: v };
        return <StatusBadge variant={m.variant} label={lang === 'tr' ? m.tr : m.en} />;
      },
    },
    {
      key: 'issued_at',
      label: lang === 'tr' ? 'Tarih' : 'Date',
      sortable: true,
      render: (v) => (
        <span className="text-xs text-[var(--text-muted)]">
          {v ? new Date(v).toLocaleDateString(lang === 'tr' ? 'tr-TR' : 'en-GB') : '-'}
        </span>
      ),
    },
    /* Detay aksiyonu */
    {
      key: '_actions',
      label: '',
      render: (_, r) => (
        <motion.button
          aria-label={`${t('admin.inv.detail_btn')} — ${r.invoice_no || r.invoice_id}`}
          onClick={(e) => { e.stopPropagation(); setSelectedId(r.invoice_id); }}
          whileHover={{ scale: 1.06 }}
          whileTap={{ scale: 0.94 }}
          className="flex items-center gap-1 px-2.5 py-1 rounded-lg border-2 border-[var(--brand-primary)] bg-[#E8F5FD] text-[var(--brand-primary)] text-[11px] font-bold transition"
        >
          <ExternalLink size={12} />
          {t('admin.inv.detail_btn')}
        </motion.button>
      ),
    },
  ];

  /* ── Durum filtreleri ───────────────────────────────────────────────────── */
  const filters = [
    { id: '',                label: lang === 'tr' ? 'Tümü'    : 'All'       },
    { id: 'issued',          label: lang === 'tr' ? 'Açık'    : 'Open'      },
    { id: 'partially_paid',  label: lang === 'tr' ? 'Kısmi'   : 'Partial'   },
    { id: 'paid',            label: lang === 'tr' ? 'Ödendi'  : 'Paid'      },
    { id: 'cancelled',       label: lang === 'tr' ? 'İptal'   : 'Cancelled' },
  ];

  return (
    <AdminLayout>
      <PageHeader
        title={t('admin.nav.invoices')}
        subtitle={lang === 'tr' ? 'Cari borç-alacak takibi' : 'Receivables tracking'}
      />

      {/* Özet kartlar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <SummaryCard
          label={lang === 'tr' ? 'Toplam Borçlandırılan' : 'Total Debited'}
          value={money(summary.total_billed)}
          color="#0EA5E9"
          bg="#E8F5FD"
        />
        <SummaryCard
          label={lang === 'tr' ? 'Tahsil Edilen' : 'Collected'}
          value={money(summary.total_collected)}
          color="#10B981"
          bg="#D7FFB8"
        />
        <SummaryCard
          label={lang === 'tr' ? 'Açık Alacak' : 'Outstanding'}
          value={money(summary.total_outstanding)}
          color="#8B5CF6"
          bg="#EDE9FE"
        />
        <SummaryCard
          label={lang === 'tr' ? 'Kayıt Sayısı' : 'Records'}
          value={summary.invoice_count || 0}
          color="#64748B"
          bg="#F1F5F9"
        />
      </div>

      {/* Durum filtresi */}
      <div className="flex flex-wrap gap-2 mb-4">
        {filters.map((f) => (
          <button
            key={f.id || 'all'}
            onClick={() => setStatus(f.id)}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold border-2 transition ${
              status === f.id
                ? 'border-[var(--brand-primary)] bg-[#E8F5FD] text-[var(--brand-primary)]'
                : 'border-gray-200 bg-white text-[var(--text-muted)]'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <LoadingState variant="table" rows={6} />
      ) : (
        <DataTable
          columns={columns}
          data={invoices}
          rowKey="invoice_id"
          pageSize={20}
          onRowClick={(row) => setSelectedId(row.invoice_id)}
          emptyTitle={lang === 'tr' ? 'Kayıt yok' : 'No records'}
          emptyDescription={
            lang === 'tr'
              ? 'Bu filtrede kayıt bulunamadı.'
              : 'No records for this filter.'
          }
        />
      )}

      {/* Kayıt Detay Modal */}
      {selectedId && (
        <InvoiceDetailModal
          invoiceId={selectedId}
          onClose={() => setSelectedId(null)}
          authHeaders={authHeaders}
          lang={lang}
          t={t}
          toast={toast}
        />
      )}
    </AdminLayout>
  );
}
